// src/utils/winterAttention.js
//
// Pass 82D §17 — the winter-core Needs-Attention model. Extends (does NOT replace) the accepted operational attention
// queue (`buildAttentionQueue`) with the winter signals a source-authoritative operation needs: missing meeting/pickup,
// source stale/failed, canonical availability conflict (a Motus-native event colliding with a source booking), and a
// SEAM for future SOURCE_DIVERGENCE and MISSING_LESSON_REPORT. Each signal appears EXACTLY ONCE with a clear reason and
// a link to the relevant record/source (§45). Reuses `detectOperationalConflicts` — NO second conflict formula.
//
// PURE (no Firestore, no React).

import { buildAttentionQueue } from './bookingCRM';
import { detectOperationalConflicts } from './operationsWorkspace';
import { isLiveBooking, isClientBooking } from './scheduleModel';
import { isSourceUnhealthy, freshnessLabel, SYNC_STATUS } from './sourceFreshness';
import { resolveBookingOperatingArea, AREA_VIA } from './operatingAreas';
import { PRODUCT_NAME } from '../config/product';
import { isCloseoutOutstanding } from './lessonCloseout';

export const ATTENTION_TYPES = {
  UNPAID: 'unpaid',
  NEEDS_ASSIGNMENT: 'needs_assignment',
  MISSING_PICKUP: 'missing_pickup',
  AVAILABILITY_CONFLICT: 'availability_conflict',
  SOURCE_STALE: 'source_stale',
  SOURCE_FAILED: 'source_failed',
  SOURCE_DIVERGENCE: 'source_divergence',       // seam (§14) — structurally prevented by the read-only source gate
  MISSING_LESSON_REPORT: 'missing_lesson_report', // seam (§17/§50) — NOT computed here; a future signal
  MISSING_OPERATING_AREA: 'missing_operating_area', // Pass 82E §32 — a record that resolves to no operating area
};

const msOf = (v) => {
  if (!v) return null;
  if (typeof v?.toDate === 'function') return v.toDate().getTime();
  if (v?.seconds) return v.seconds * 1000;
  if (v instanceof Date) return v.getTime();
  const d = new Date(v); return Number.isNaN(d.getTime()) ? null : d.getTime();
};

const isUpcoming = (b, nowMs) => { const m = msOf(b.startAt); return m != null && (!nowMs || m >= nowMs); };
const hasPickup = (b) => !!(String(b.locationLabel || '').trim() || String(b.meetingPoint || '').trim() || String(b.pickup || '').trim() || String(b.transportInfo || '').trim());

// Compose the full winter attention list. `sources` = [{ sourceId, label, status }] freshness (from deriveSyncFreshness);
// `divergences` = optional explicit divergence findings (empty by construction while the read-only gate holds §14).
export function buildWinterAttention({
  bookings = [], bookingRequests = [], members = [], sources = [], divergences = [], conflicts = null, nowMs = 0,
  // Pass 82E — when the org operates areas, pass the resolver context to flag records that resolve to NO area. Omitted
  // (null) for an org that does not operate areas → no MISSING_OPERATING_AREA noise.
  areaResolverCtx = null,
  // Pass 84 — the delivered lessonInstances (joined to bookings by bookingId) so a DELIVERED lesson whose coaching
  // closeout is still missing can surface. Omitted ([]) → no MISSING_LESSON_REPORT signal (existing callers unchanged).
  lessons = [],
} = {}) {
  const items = [];
  const seen = new Set();
  const add = (it) => { if (it && it.id && !seen.has(it.id)) { seen.add(it.id); items.push(it); } };

  // (1) Reuse the accepted base queue (unpaid, needs_assignment, pending_request, change_request, follow_up).
  for (const it of buildAttentionQueue({ bookings, bookingRequests, members })) add(it);

  const live = bookings.filter(isLiveBooking);

  // (2) MISSING MEETING/PICKUP — an upcoming confirmed client lesson with no meeting point.
  for (const b of live) {
    if (isClientBooking(b) && b.status === 'confirmed' && b.startAt && isUpcoming(b, nowMs) && !hasPickup(b)) {
      add({
        id: `pickup-${b.id}`, type: ATTENTION_TYPES.MISSING_PICKUP, priority: 2,
        label: `Missing meeting point — ${b.requesterName || 'Client'}`,
        detail: 'Add a meeting/pickup location', bookingId: b.id, timestamp: b.startAt,
      });
    }
  }

  // (3) CANONICAL AVAILABILITY CONFLICT — reuse detectOperationalConflicts (covers Motus-native work colliding with a
  //     source booking on the same human/time). One item per conflicting booking, de-duped. §16.
  const conflictSet = conflicts instanceof Set ? conflicts : detectOperationalConflicts(bookings);
  for (const b of live) {
    if (conflictSet.has(b.id)) {
      add({
        id: `conflict-${b.id}`, type: ATTENTION_TYPES.AVAILABILITY_CONFLICT, priority: 1,
        label: `Schedule conflict — ${b.instructorNameSnapshot || b.requesterName || 'Instructor'}`,
        detail: 'Two overlapping commitments for the same person', bookingId: b.id, timestamp: b.startAt,
      });
    }
  }

  // (4) SOURCE STALE / FAILED — from the existing sync freshness (never "0 bookings", §12). One item per unhealthy source.
  for (const s of sources) {
    if (isSourceUnhealthy(s.status)) {
      add({
        id: `source-${s.sourceId}`,
        type: s.status === SYNC_STATUS.FAILED ? ATTENTION_TYPES.SOURCE_FAILED : ATTENTION_TYPES.SOURCE_STALE,
        priority: 1,
        label: `${s.label || 'Schedule source'} — ${freshnessLabel(s.status)}`,
        detail: 'Instructors may be seeing an out-of-date schedule', sourceId: s.sourceId, timestamp: s.lastSuccessMs || null,
      });
    }
  }

  // (5) MISSING OPERATING AREA (§32) — a confirmed record that resolves to NO area, once the org operates areas. This is
  //     operationally missing (needs an area), distinct from source free-text that is merely not-yet-structured (which
  //     is NOT flagged here — a source booking still resolves to its source-config area, so it never trips this).
  if (areaResolverCtx) {
    for (const b of live) {
      if (!isClientBooking(b)) continue;
      const { via } = resolveBookingOperatingArea(b, areaResolverCtx);
      if (via === AREA_VIA.UNRESOLVED) {
        add({
          id: `area-${b.id}`, type: ATTENTION_TYPES.MISSING_OPERATING_AREA, priority: 2,
          label: `Needs operating area — ${b.requesterName || b.instructorNameSnapshot || 'Booking'}`,
          detail: 'Assign this booking to an operating area', bookingId: b.id, timestamp: b.startAt,
        });
      }
    }
  }

  // (7) MISSING LESSON REPORT (Pass 84 §14) — a DELIVERED client lesson whose human coaching closeout is still
  //     missing. Delivery is the canonical `isDeliveryBearing` predicate ONLY (via isCloseoutOutstanding); a mere
  //     past booking, a cancelled booking, or a started-but-empty draft never trips this — so old source rows that
  //     were never canonically delivered raise NO false-critical alert. "delivered, closeout missing" is distinct
  //     from "delivery unconfirmed" (no delivery-bearing lesson), which is deliberately NOT surfaced here.
  if (lessons && lessons.length) {
    const lessonByBooking = new Map();
    for (const l of lessons) { const bk = String(l?.bookingId || '').trim(); if (bk && !lessonByBooking.has(bk)) lessonByBooking.set(bk, l); }
    for (const b of live) {
      if (!isClientBooking(b) || b.status === 'canceled' || b.status === 'cancelled') continue;
      const lesson = lessonByBooking.get(String(b.id));
      if (lesson && isCloseoutOutstanding(lesson)) {
        add({
          id: `report-${b.id}`, type: ATTENTION_TYPES.MISSING_LESSON_REPORT, priority: 2,
          label: `Closeout needed — ${b.requesterName || b.instructorNameSnapshot || 'Lesson'}`,
          detail: 'This lesson was delivered — add the coaching closeout', bookingId: b.id, timestamp: b.startAt,
        });
      }
    }
  }

  // (6) SOURCE DIVERGENCE — a seam (§14). Structurally prevented by the read-only source gate; only surfaces if an
  //     explicit divergence finding is supplied (e.g. a future audit tool). Empty in normal operation.
  for (const d of divergences) {
    add({
      id: `divergence-${d.bookingId || d.id}`, type: ATTENTION_TYPES.SOURCE_DIVERGENCE, priority: 1,
      label: `Source divergence — ${d.label || d.field || 'record'}`,
      detail: d.detail || `${PRODUCT_NAME} differs from the authoritative source`, bookingId: d.bookingId || '', field: d.field || '', timestamp: d.timestamp || null,
    });
  }

  // Stable order: priority asc, then most-imminent timestamp.
  items.sort((a, b) => (a.priority - b.priority) || (msOf(a.timestamp) || Infinity) - (msOf(b.timestamp) || Infinity));
  return items;
}
